import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../hooks/useNotification';
import { resendEmailConfirmation } from '../services/supabase';
import { getUserMemories, addToCart, getDisplayUrl } from '../services/memoryService';
import { getUserProfile } from '../services/userProfileService';
import './Home.css';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import { Mail, Plus, ShoppingCart } from 'lucide-react';
import logger from '../utils/logger';

function Home() {
  // eslint-disable-next-line no-unused-vars
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingToCart, setAddingToCart] = useState(null);
  const [displayUrls, setDisplayUrls] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const { user, loading: authLoading, signOut } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [resending, setResending] = useState(false);

  // Commenting out unused functions - keeping for potential future use
  // const toggleMenu = () => {
  //   setIsMenuOpen(!isMenuOpen);
  // };

  // const closeMenu = () => {
  //   setIsMenuOpen(false);
  // };

  // const handleLogout = async () => {
  //   try {
  //     await signOut();
  //     navigate('/');
  //   } catch (error) {
  //     logger.error('Logout error:', error);
  //   }
  // };

  const handleEditMemory = (memory) => {
    // Navigate to Create page with pre-filled data
    navigate('/create', {
      state: {
        editMode: true,
        memoryData: memory,
      },
    });
  };

  const handleResendConfirm = async () => {
    if (!user?.email) return;
    try {
      setResending(true);
      const { error } = await resendEmailConfirmation(user.email);
      if (error) {
        if ((error.message || '').toLowerCase().includes('already registered')) {
          showSuccess('Confirmation email resent. Check your inbox or spam.');
        } else {
          showError(`Failed to resend confirmation email: ${error.message}`);
        }
      } else {
        showSuccess('Confirmation email resent. Check your inbox.');
      }
    } catch (e) {
      showError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const loadDisplayUrls = async (memories) => {
    const urlPromises = memories.map(async (memory) => {
      if (!memory.photo_url) return null;

      try {
        const displayUrl = await getDisplayUrl(memory.photo_url);
        return { id: memory.id, displayUrl };
      } catch (error) {
        logger.error('Error getting display URL for memory:', memory.id, error);
        return { id: memory.id, displayUrl: memory.photo_url };
      }
    });

    const results = await Promise.all(urlPromises);
    const urlMap = {};
    results.forEach(result => {
      if (result) {
        urlMap[result.id] = result.displayUrl;
      }
    });
    setDisplayUrls(urlMap);
  };

  const handleAddToCart = async (memory) => {
    if (!user) {
      showSuccess('Please log in to add items to cart', {
        text: 'OK',
        onClick: () => navigate('/auth'),
      });
      return;
    }

    try {
      setAddingToCart(memory.id);

      await addToCart(memory.id, 1, memory.price, user.id);

      showSuccess('This tiniego has been added to your cart.', {
        text: 'Go to Cart',
        onClick: () => navigate('/cart'),
        icon: <ShoppingCart size={18} />,
      });
    } catch (err) {
      logger.error('Error adding to cart:', err);
      showSuccess('Failed to add to cart. Please try again.', {
        text: 'OK',
        onClick: () => logger.debug('OK clicked'),
      });
    } finally {
      setAddingToCart(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      uploaded: { text: 'Uploaded', class: 'status-uploaded' },
      processing_cartoon: { text: 'Sketching', class: 'status-processing' },
      completed_cartoon: { text: 'Sketched', class: 'status-uploaded' },
      processing_3d: { text: 'Assembling', class: 'status-processing' },
      completed: { text: 'Completed', class: 'status-completed' },
      failed: { text: 'Failed', class: 'status-failed' },
    };

    const config = statusConfig[status] || statusConfig.uploaded;
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  // Commenting out unused function - keeping for potential future use
  // const formatDate = (dateString) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //   });
  // };

  const getDisplayName = () => {
    // First try to get first_name from user profile
    if (userProfile?.first_name) {
      return userProfile.first_name;
    }
    // Fallback to email username if first_name is not available
    return user?.email?.split('@')[0] || 'User';
  };

  // Show welcome toast after signup confirmation redirect and clean the URL
  useEffect(() => {
    if (searchParams.get('type') === 'signup') {
      showSuccess('🎉 Welcome! Your email has been confirmed and you are now signed in.');
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, showSuccess]);

  useEffect(() => {
    const fetchData = async () => {
      if (authLoading) {
        return;
      }

      if (!user) {
        setError('Please log in to view your memories');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch user profile and memories in parallel
        const [profileResult, userMemories] = await Promise.all([
          getUserProfile(),
          getUserMemories()
        ]);

        if (profileResult.data) {
          setUserProfile(profileResult.data);
        } else if (profileResult.error) {
          logger.warn('Error fetching user profile:', profileResult.error);
        }

        setMemories(userMemories);
        await loadDisplayUrls(userMemories);
      } catch (err) {
        logger.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, authLoading]);

  if (loading) {
    return (
      <div className="view-models-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your memories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-models-page">
      <Navbar />

      <div className="content">
        <div className="content-container">
          {!!user && !user.email_confirmed_at && (
            <div
              className="email-warning-banner"
              style={{
                background: '#fff3cd',
                border: '1px solid #ffeeba',
                color: '#856404',
                padding: '12px 16px',
                borderRadius: '8px',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <strong>Email not confirmed.</strong> You can use the app now, but you'll need to
                confirm your email before purchasing.
              </div>
              <div>
                <Button
                  variant="secondary"
                  className="resend-confirm-btn"
                  onClick={handleResendConfirm}
                  disabled={resending}
                  icon={Mail}
                  style={{
                    backgroundColor: '#856404',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {resending ? 'Sending...' : 'Resend confirmation email'}
                </Button>
              </div>
            </div>
          )}
          <div className="models-container">
            <div className="page-header">
              <h1>
                Welcome back,<br></br>
                {getDisplayName()}!
              </h1>
              <p>View and manage your tiniegos</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            {memories.length === 0 && !error ? (
              <div className="empty-state">
                <img src="/empty_image.png" alt="Empty state" className="empty-state-image" loading="lazy" />
                <Button
                  variant="primary"
                  className="upload-btn"
                  fullWidth
                  onClick={() => navigate('/create')}
                  icon={Plus}
                >
                  Let's create your first tiniego!
                </Button>
              </div>
            ) : (
              <div className="memories-section">
                <div className="section-actions">
                  <Button
                    variant="secondary"
                    className="cart-section-btn"
                    fullWidth
                    onClick={() => navigate('/cart')}
                    icon={ShoppingCart}
                  >
                    View Cart
                  </Button>
                  <div
                    className="create-memory-card"
                    onClick={() => navigate('/create')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="create-memory-content">
                      <Plus className="create-memory-icon" size={20} />
                      <h3 className="create-memory-title">Create a tiniego</h3>
                    </div>
                  </div>
                </div>
                <div className="memories-grid">
                  {memories.map((memory) => (
                    <div
                      key={memory.id}
                      className="memory-card clickable-card"
                      onClick={() => handleEditMemory(memory)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="memory-image">
                        {memory.photo_url ? (
                          <img
                            src={displayUrls[memory.id] || memory.photo_url}
                            alt={memory.title}
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = '/empty_image.png';
                            }}
                          />
                        ) : (
                          <div className="no-image">
                            <span>📸</span>
                          </div>
                        )}
                        {getStatusBadge(memory.status)}
                      </div>

                      <div className="memory-content">
                        <h3 className="memory-title">{memory.title}</h3>
                        {memory.description && (
                          <p className="memory-description">{memory.description}</p>
                        )}

                        <div className="memory-actions">
                          <Button
                            variant="primary"
                            className="add-to-cart-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(memory);
                            }}
                            disabled={addingToCart === memory.id || memory.status !== 'completed'}
                            title={
                              memory.status !== 'completed'
                                ? 'Only completed models can be added to cart'
                                : 'Add to cart'
                            }
                            icon={ShoppingCart}
                          >
                            {addingToCart === memory.id ? '⏳ Adding...' : 'Add to Cart'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;

// Toast mounted at page root to slide over content
/* eslint-disable react/no-unknown-property */
/* Render toast at the bottom */
